import type {
  AdminV2EntitlementRecord,
  AdminV2FeatureAccessEnvironment,
  AdminV2FeatureAccessSummaryResponse,
} from "@/types/adminV2";

/**
 * Pure logic for the admin Feature flags console.
 *
 * @remarks
 * Resolution mirrors the runtime gate: a record grants access only when it is
 * enabled and inside its `[startsAt, expiresAt)` window, and an org-scoped row
 * overrides the global row. Keeping this in one tested module avoids drift
 * between the console's displayed state and what customers actually get.
 */

const EMPTY_ORGANISATIONS: AdminV2FeatureAccessSummaryResponse["organisations"] =
  [];

/**
 * True when a record currently grants access: enabled, started, not expired.
 */
export const isEntitlementRecordActive = (
  record: AdminV2EntitlementRecord,
  now: number,
): boolean => {
  if (!record.isEnabled) {
    return false;
  }
  if (record.startsAt) {
    const startsAt = Date.parse(record.startsAt);
    if (Number.isFinite(startsAt) && startsAt > now) {
      return false;
    }
  }
  if (record.expiresAt) {
    const expiresAt = Date.parse(record.expiresAt);
    if (Number.isFinite(expiresAt) && expiresAt <= now) {
      return false;
    }
  }
  return true;
};

/** True when the global (all-organisations) row is currently on. */
export const isGlobalEnabled = (
  summary: AdminV2FeatureAccessSummaryResponse | undefined,
  now: number,
): boolean =>
  summary?.global ? isEntitlementRecordActive(summary.global, now) : false;

/** Count of org-scoped rows that currently grant access. */
export const countEnabledOrganisations = (
  summary: AdminV2FeatureAccessSummaryResponse | undefined,
  now: number,
): number =>
  (summary?.organisations ?? EMPTY_ORGANISATIONS).filter((organisation) =>
    isEntitlementRecordActive(organisation.record, now),
  ).length;

/**
 * Count of org-scoped rows that currently do NOT grant access. While the
 * global row is on, these are the explicit exclusions.
 */
export const countExcludedOrganisations = (
  summary: AdminV2FeatureAccessSummaryResponse | undefined,
  now: number,
): number =>
  (summary?.organisations ?? EMPTY_ORGANISATIONS).filter(
    (organisation) => !isEntitlementRecordActive(organisation.record, now),
  ).length;

export type FeatureAccessStatus =
  | { kind: "all"; excludedCount: number }
  | { kind: "scoped"; enabledCount: number }
  | { kind: "off" };

/**
 * Headline status for a feature: on for everyone (minus any exclusions), on
 * for N specific orgs, or off.
 */
export const resolveFeatureAccessStatus = (
  summary: AdminV2FeatureAccessSummaryResponse | undefined,
  now: number,
): FeatureAccessStatus => {
  if (isGlobalEnabled(summary, now)) {
    return {
      kind: "all",
      excludedCount: countExcludedOrganisations(summary, now),
    };
  }
  const enabledCount = countEnabledOrganisations(summary, now);
  return enabledCount > 0 ? { kind: "scoped", enabledCount } : { kind: "off" };
};

const organisationWord = (count: number): string =>
  `${count} organisation${count === 1 ? "" : "s"}`;

export const formatFeatureAccessStatusLabel = (
  status: FeatureAccessStatus,
): string => {
  switch (status.kind) {
    case "all":
      return status.excludedCount > 0
        ? `On for all organisations (${organisationWord(
            status.excludedCount,
          )} excluded)`
        : "On for all organisations";
    case "scoped":
      return `On for ${organisationWord(status.enabledCount)}`;
    case "off":
      return "Off";
  }
};

/**
 * Per-organisation access label, accounting for the global override.
 *
 * @remarks
 * When the global switch is on, an org row that is off is an explicit exclusion;
 * when the global switch is off, an org row that is on is an explicit grant.
 */
export const formatOrganisationAccessLabel = (
  record: AdminV2EntitlementRecord,
  globalEnabled: boolean,
  now: number,
): string => {
  const active = isEntitlementRecordActive(record, now);
  if (globalEnabled) {
    return active ? "On (via all)" : "Excluded";
  }
  return active ? "On" : "Off";
};

/**
 * Whether the org picker should grant (true) or exclude (false) a newly added
 * organisation. When the feature is on for everyone, adding an org means
 * excluding it; otherwise adding an org means turning it on for that org.
 */
export const shouldAddOrganisationEnabled = (globalEnabled: boolean): boolean =>
  !globalEnabled;

export const formatAddOrganisationActionLabel = (
  globalEnabled: boolean,
): string => (globalEnabled ? "Exclude" : "Enable");

/** Org IDs that already have a row for this feature (to hide them from search). */
export const collectKnownOrganisationIds = (
  summary: AdminV2FeatureAccessSummaryResponse | undefined,
): Set<string> =>
  new Set(
    (summary?.organisations ?? EMPTY_ORGANISATIONS).map(
      (organisation) => organisation.organisationId,
    ),
  );

interface EntitlementUpsertPayload {
  organisationId?: string;
  entitlementKey: string;
  environment: AdminV2FeatureAccessEnvironment;
  isEnabled: boolean;
  source: "admin";
  value: Record<string, never>;
}

/** Payload to turn a feature on/off for ALL organisations (global row). */
export const buildApplyToAllPayload = (
  entitlementKey: string,
  environment: AdminV2FeatureAccessEnvironment,
  enable: boolean,
): EntitlementUpsertPayload => ({
  entitlementKey,
  environment,
  isEnabled: enable,
  source: "admin",
  value: {},
});

/** Payload to set a single organisation's access (org-scoped row). */
export const buildOrganisationAccessPayload = (
  entitlementKey: string,
  environment: AdminV2FeatureAccessEnvironment,
  organisationId: string,
  enable: boolean,
): EntitlementUpsertPayload => ({
  entitlementKey,
  organisationId,
  environment,
  isEnabled: enable,
  source: "admin",
  value: {},
});
